import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {useNavigate} from "react-router-dom";
import {observer} from "mobx-react-lite";
import {Context} from "../../index";
import ShipmentForm from "./ShipmentForm";
import YandexPvzPickerModal from "./YandexPvzPickerModal";
import {createNewOrder} from "../../http/ordersAPI";
import {addItemToCart, deleteItemFromCart, fetchCartItems} from "../../http/cartAPI";
import {fetchShipmentInfo} from "../../http/userAPI";
import {fetchWarehousePickupPoint} from "../../http/yandexB2bAPI";
import {addToGuestCart, clearGuestCart, guestCartToOrderItemRequests, readGuestCart} from "../../util/guestCart";
import {effectiveStockCap} from "../../util/cartLineAvailable";
import {yandexNddStationId, yandexNddWeightG} from "../../util/yandexNddConfig";
import {fetchYandexPvzOfferPricingRub} from "../../http/yandexPvzOfferAPI";
import {
    ORDERS_ROUTE,
    ORDER_DELIVERY_MANUAL,
    ORDER_DELIVERY_YANDEX_PICKUP,
    YANDEX_PVZ_ADDRESS_PREFIX,
    stripLegacyYandexPvzPrefix,
} from "../../util/Constants";

const emptyShipment = () => ({
    name: '',
    address: '',
    phone: '',
    email: '',
    commentary: '',
});

/** Как в бэкенде PHONE_PATTERN */
const PHONE_OK = /^[+]?[0-9\s().-]{10,25}$/;

const DELIVERY_MANUAL = ORDER_DELIVERY_MANUAL;
const DELIVERY_YANDEX = ORDER_DELIVERY_YANDEX_PICKUP;

function notifyCartChanged() {
    window.dispatchEvent(new CustomEvent('lutica-cart-changed'));
}

/**
 * Ответ API профиля (shipment) → что подставить в форму оформления.
 * Легаси: в address мог быть префикс ПВЗ — в форме показываем только текст пункта.
 */
function checkoutPrefillFromShipmentPayload(d) {
    const rawAddress = d.address ?? '';
    const yandexPointId = (d.yandexPickupPointId && String(d.yandexPickupPointId).trim()) || '';
    const hadLegacyPvzPrefix = rawAddress.startsWith(YANDEX_PVZ_ADDRESS_PREFIX);
    const addressForForm = stripLegacyYandexPvzPrefix(rawAddress);
    const preferYandexDelivery = Boolean(yandexPointId) || hadLegacyPvzPrefix;

    return {
        addressForForm,
        yandexPointId,
        preferYandexDelivery,
        profileHasPvzTextButNoId: hadLegacyPvzPrefix && !yandexPointId,
        manualAddressToRestore: preferYandexDelivery ? '' : rawAddress,
        person: {
            name: d.name ?? '',
            phone: d.phone ?? '',
            email: d.email ?? '',
            commentary: d.commentary ?? '',
        },
    };
}

const OrderCreation = observer(() => {
    const {user, cart} = useContext(Context);
    const navigate = useNavigate();
    const [shipment, setShipment] = useState(emptyShipment);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);
    const [serverCartLines, setServerCartLines] = useState([]);
    const [authCheckoutLoading, setAuthCheckoutLoading] = useState(false);

    const [deliveryType, setDeliveryType] = useState(DELIVERY_MANUAL);
    const [yandexPointId, setYandexPointId] = useState('');
    /** Стоимость доставки до ПВЗ только из расчёта виджета (событие / pointOfferMap). В shipment не кладём. */
    const [yandexFeeRub, setYandexFeeRub] = useState(null);
    /** Станция отгрузки для виджета (API 2.02 на бэкенде или fallback из env). */
    const [shipFromStationId, setShipFromStationId] = useState(yandexNddStationId);
    const [warehouseMeta, setWarehouseMeta] = useState(null);
    /** Не открывать карту до ответа API склада. */
    const [yandexShipStationReady, setYandexShipStationReady] = useState(false);
    /** Фоновый расчёт оффера (тот же API, что у виджета), без открытия модалки. */
    const [yandexFeePrefetching, setYandexFeePrefetching] = useState(false);

    const [pvzModalOpen, setPvzModalOpen] = useState(false);
    const [pvzModalRemountKey, setPvzModalRemountKey] = useState(0);
    const [pvzModalSearchPreset, setPvzModalSearchPreset] = useState('');

    const savedManualAddressRef = useRef('');
    /** Адрес, введённый вручную до переключения на Яндекс — подставляется в поиск виджета. */
    const manualAddressBeforeYandexRef = useRef('');
    /** Снимок ПВЗ при уходе с Яндекс-доставки — восстанавливается при возврате. */
    const savedYandexPointIdRef = useRef('');
    const savedYandexPvzAddressRef = useRef('');
    const savedYandexFeeRubRef = useRef(null);
    /** В профиле есть текст ПВЗ, но нет ID пункта (старые данные). */
    const [pvzMissingIdFromProfile, setPvzMissingIdFromProfile] = useState(false);
    /** Сбрасываем, чтобы перечитать гостевую корзину из localStorage после +/- / события */
    const [, setGuestCartBump] = useState(0);
    const guestLines = user.isAuth ? [] : readGuestCart();

    useEffect(() => {
        if (!user.isAuth) {
            setServerCartLines([]);
            setAuthCheckoutLoading(false);
            return;
        }
        let cancelled = false;
        setAuthCheckoutLoading(true);
        Promise.all([fetchShipmentInfo(), fetchCartItems()])
            .then(([shipmentRes, lines]) => {
                if (cancelled) return;
                const pre = checkoutPrefillFromShipmentPayload(shipmentRes.data || {});
                setPvzMissingIdFromProfile(pre.profileHasPvzTextButNoId);
                if (pre.preferYandexDelivery) {
                    setDeliveryType(DELIVERY_YANDEX);
                    setYandexPointId(pre.yandexPointId);
                    savedManualAddressRef.current = '';
                } else {
                    savedManualAddressRef.current = pre.manualAddressToRestore;
                }
                setShipment({
                    ...pre.person,
                    address: pre.addressForForm,
                });
                const safeLines = Array.isArray(lines) ? lines : [];
                setServerCartLines(safeLines);
                cart.setTotalFromLines(safeLines);
            })
            .catch(() => {
                if (!cancelled) {
                    setServerCartLines([]);
                }
            })
            .finally(() => {
                if (!cancelled) setAuthCheckoutLoading(false);
            });
        return () => {
            cancelled = true;
        };
    // cart не в deps: иначе при любом update корзины сбрасывается доставка/ПВЗ
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.isAuth]);

    const syncCartFromEvent = useCallback(() => {
        if (user.isAuth) {
            fetchCartItems()
                .then((lines) => {
                    setServerCartLines(Array.isArray(lines) ? lines : []);
                    cart.setTotalFromLines(Array.isArray(lines) ? lines : []);
                })
                .catch(() => {});
        } else {
            setGuestCartBump((v) => v + 1);
            cart.setTotalFromLines(readGuestCart());
        }
    }, [user.isAuth, cart]);

    useEffect(() => {
        const onSync = () => {
            syncCartFromEvent();
        };
        window.addEventListener('lutica-cart-changed', onSync);
        return () => window.removeEventListener('lutica-cart-changed', onSync);
    }, [syncCartFromEvent]);

    useEffect(() => {
        if (deliveryType !== DELIVERY_YANDEX) {
            setYandexShipStationReady(false);
            return undefined;
        }
        let cancelled = false;
        setYandexShipStationReady(false);
        fetchWarehousePickupPoint()
            .then((d) => {
                if (cancelled) {
                    return;
                }
                if (d?.pickupPointId) {
                    setShipFromStationId(String(d.pickupPointId).trim());
                    setWarehouseMeta({
                        fromApi: !!d.fromApi,
                        fullAddress: d.fullAddress || '',
                        name: d.name || '',
                    });
                } else {
                    setWarehouseMeta(null);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setWarehouseMeta(null);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setYandexShipStationReady(true);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [deliveryType]);

    const yandexOfferKeyRef = useRef('');
    useEffect(() => {
        if (deliveryType !== DELIVERY_YANDEX || !yandexShipStationReady || pvzMissingIdFromProfile) {
            setYandexFeePrefetching(false);
            return undefined;
        }
        const sid = String(shipFromStationId || '').trim();
        const pid = String(yandexPointId || '').trim();
        if (!sid || !pid) {
            setYandexFeePrefetching(false);
            return undefined;
        }
        if (typeof yandexFeeRub === 'number' && !Number.isNaN(yandexFeeRub)) {
            setYandexFeePrefetching(false);
            return undefined;
        }
        const key = `${sid}|${pid}`;
        yandexOfferKeyRef.current = key;
        const ac = new AbortController();
        let cancelled = false;
        setYandexFeePrefetching(true);
        fetchYandexPvzOfferPricingRub({
            sourcePlatformStationId: sid,
            destinationPointId: pid,
            weightGrossG: yandexNddWeightG,
            signal: ac.signal,
        })
            .then((r) => {
                if (cancelled || yandexOfferKeyRef.current !== key) {
                    return;
                }
                if (r.pricingRub != null) {
                    setYandexFeeRub(r.pricingRub);
                }
            })
            .finally(() => {
                if (!cancelled && yandexOfferKeyRef.current === key) {
                    setYandexFeePrefetching(false);
                }
            });
        return () => {
            cancelled = true;
            ac.abort();
        };
    }, [
        deliveryType,
        yandexShipStationReady,
        shipFromStationId,
        yandexPointId,
        yandexFeeRub,
        pvzMissingIdFromProfile,
    ]);

    const onDeliveryKindChange = (next) => {
        if (next === DELIVERY_MANUAL) {
            if (deliveryType === DELIVERY_YANDEX) {
                savedYandexPointIdRef.current = String(yandexPointId || '').trim();
                savedYandexPvzAddressRef.current = String(shipment.address || '').trim();
                savedYandexFeeRubRef.current =
                    typeof yandexFeeRub === 'number' && !Number.isNaN(yandexFeeRub) ? yandexFeeRub : null;
            }
            const fromSavedManual = (savedManualAddressRef.current || '').trim();
            const fromCurrentField = (shipment.address || '').trim();
            const addressRestored = fromSavedManual || fromCurrentField;
            setShipment((s) => ({...s, address: addressRestored}));
            setYandexPointId('');
            setYandexFeeRub(null);
            manualAddressBeforeYandexRef.current = '';
            setPvzMissingIdFromProfile(false);
        } else {
            savedManualAddressRef.current = shipment.address || '';
            manualAddressBeforeYandexRef.current = (shipment.address || '').trim();
            const pid = savedYandexPointIdRef.current.trim();
            const pvzLine = savedYandexPvzAddressRef.current.trim();
            if (pid) {
                setYandexPointId(pid);
                setYandexFeeRub(savedYandexFeeRubRef.current);
            } else {
                setYandexPointId('');
                setYandexFeeRub(null);
            }
            setShipment((s) => ({...s, address: pvzLine}));
            setPvzMissingIdFromProfile(false);
        }
        setDeliveryType(next);
        setError('');
    };

    const onYandexSelection = ({pointId, addressLine, feeRub}) => {
        setPvzMissingIdFromProfile(false);
        setYandexPointId(pointId || '');
        setYandexFeeRub(typeof feeRub === 'number' && !Number.isNaN(feeRub) ? feeRub : null);
        const line = (addressLine || '').trim();
        setShipment((s) => ({
            ...s,
            address: line,
        }));
    };

    const openPvzModal = () => {
        const fromCurrentPvz = (shipment.address || '').trim();
        const seed = (fromCurrentPvz || manualAddressBeforeYandexRef.current || '').trim();
        setPvzModalSearchPreset(seed);
        setPvzModalRemountKey((k) => k + 1);
        setPvzModalOpen(true);
    };

    const linesForTotals = user.isAuth ? serverCartLines : guestLines;
    const itemsSubtotal = linesForTotals.reduce((sum, line) => {
        const qty = line.itemCount ?? 0;
        return sum + Number(line.price) * qty;
    }, 0);

    const resolvedYandexFee =
        deliveryType === DELIVERY_YANDEX &&
        typeof yandexFeeRub === 'number' &&
        !Number.isNaN(yandexFeeRub)
            ? yandexFeeRub
            : null;
    const yandexFeeValid =
        deliveryType !== DELIVERY_YANDEX ||
        (resolvedYandexFee != null && resolvedYandexFee >= 0);

    const yandexPvzAddressOk =
        deliveryType !== DELIVERY_YANDEX || Boolean(shipment.address?.trim());

    const yandexPointOk = deliveryType !== DELIVERY_YANDEX || Boolean(yandexPointId?.trim());

    /** Есть id пункта и непустой адрес (в т.ч. из профиля) — пункт считается выбранным в UI. */
    const yandexPickupChosen =
        deliveryType !== DELIVERY_YANDEX || (yandexPointOk && yandexPvzAddressOk);

    /** Оформить заказ можно только когда сумма доставки уже пришла из расчёта виджета. */
    const yandexCheckoutReady =
        deliveryType !== DELIVERY_YANDEX || (yandexPickupChosen && yandexFeeValid);

    /** ПВЗ выбран, а сумма доставки ещё не готова — блокируем кнопку (отдельно от валидации по клику). */
    const yandexFeeBlocking =
        deliveryType === DELIVERY_YANDEX && yandexPickupChosen && !yandexFeeValid;
    const placeOrderButtonTitle = yandexFeeBlocking
        ? yandexFeePrefetching
            ? 'Стоимость доставки ещё рассчитывается. Подождите немного.'
            : 'Стоимость доставки пока не получена. Откройте карту и нажмите «Продолжить» у пункта выдачи или попробуйте снова.'
        : '';

    const lineKey = (line) => (user.isAuth ? line.id : line.itemId);

    const handleLineIncrease = (line) => {
        setError('');
        if (user.isAuth) {
            addItemToCart(line.id)
                .then(() => {
                    notifyCartChanged();
                })
                .catch((err) => setError(err.response?.data?.message || err.message));
        } else {
            addToGuestCart(
                {id: line.itemId, title: line.title, price: line.price, count: line.count},
                1,
            );
            notifyCartChanged();
        }
    };

    const handleLineDecrease = (line) => {
        setError('');
        if (user.isAuth) {
            deleteItemFromCart(line.id)
                .then(() => {
                    notifyCartChanged();
                })
                .catch((err) => setError(err.response?.data?.message || err.message));
        } else {
            addToGuestCart(
                {id: line.itemId, title: line.title, price: line.price, count: line.count},
                -1,
            );
            notifyCartChanged();
        }
    };

    const placeOrder = () => {
        setError('');
        setAttemptedSubmit(true);
        if (!shipment.name?.trim() || !shipment.email?.trim()) {
            setError('Заполните ФИО и email');
            return;
        }
        if (!shipment.phone?.trim() || !PHONE_OK.test(shipment.phone.trim())) {
            setError('Укажите телефон: 10–25 символов, цифры и символы + ( ) . -');
            return;
        }
        if (deliveryType === DELIVERY_MANUAL) {
            if (!shipment.address?.trim()) {
                setError('Укажите адрес доставки');
                return;
            }
        } else {
            if (!yandexPointOk) {
                setError('Выберите пункт выдачи на карте (кнопка «Выбрать на карте»)');
                return;
            }
            if (!yandexPvzAddressOk) {
                setError('Подтвердите ПВЗ в карте (кнопка «Продолжить» в виджете)');
                return;
            }
            if (!yandexFeeValid) {
                setError(
                    yandexFeePrefetching
                        ? 'Подождите: идёт расчёт стоимости доставки до ПВЗ.'
                        : 'Не удалось получить стоимость доставки. Откройте карту и нажмите «Продолжить» у пункта или попробуйте снова.',
                );
                return;
            }
        }
        if (user.isAuth && serverCartLines.length === 0) {
            setError('Корзина пуста. Добавьте товары в корзину.');
            return;
        }
        if (!user.isAuth && guestLines.length === 0) {
            setError('Корзина пуста. Добавьте товары или войдите в аккаунт.');
            return;
        }
        setSubmitting(true);
        const orderItemRequests = user.isAuth ? [] : guestCartToOrderItemRequests(guestLines);
        const deliveryFeeForOrder =
            deliveryType === DELIVERY_YANDEX && resolvedYandexFee != null
                ? Number(resolvedYandexFee.toFixed(2))
                : null;
        createNewOrder(
            {
                shipmentRequest: {
                    ...shipment,
                    yandexPickupPointId:
                        deliveryType === DELIVERY_YANDEX ? yandexPointId.trim() || null : null,
                },
                orderItemRequests,
                deliveryType,
                deliveryFee: deliveryFeeForOrder,
                yandexPickupPointId: deliveryType === DELIVERY_YANDEX ? yandexPointId.trim() : null,
            },
            user.isAuth,
        )
            .then((order) => {
                if (!user.isAuth) {
                    clearGuestCart();
                }
                cart.refreshTotals(user.isAuth);
                navigate(ORDERS_ROUTE + '/' + order.id);
            })
            .catch((err) => setError(err.response?.data?.message || err.message || 'Ошибка оформления'))
            .finally(() => setSubmitting(false));
    };

    const pvzLineDisplay = (shipment.address || '').trim();

    const yandexPickupSlot =
        deliveryType === DELIVERY_YANDEX ? (
            <>
                {pvzMissingIdFromProfile ? (
                    <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        В профиле сохранён только текст адреса ПВЗ без идентификатора пункта. Откройте карту и выберите
                        пункт заново.
                    </p>
                ) : null}
                {warehouseMeta?.fromApi && warehouseMeta.fullAddress ? (
                    <p className="text-xs text-bark-600">
                        Станция отгрузки: {warehouseMeta.name ? `${warehouseMeta.name} — ` : ''}
                        {warehouseMeta.fullAddress}
                    </p>
                ) : null}
                {!yandexShipStationReady ? (
                    <p className="text-sm text-bark-500">Загрузка параметров доставки…</p>
                ) : null}
                <div
                    className={`rounded-2xl border px-3 py-3 text-sm ${
                        attemptedSubmit && deliveryType === DELIVERY_YANDEX && !yandexCheckoutReady
                            ? 'border-red-200 bg-red-50/80 text-red-900'
                            : yandexPickupChosen
                              ? 'border-pine-200 bg-pine-50/60 text-bark-900'
                              : 'border-amber-200 bg-amber-50/50 text-bark-900'
                    }`}
                >
                    <div className="font-medium">
                        {yandexPickupChosen ? 'Пункт выдачи выбран' : 'Пункт выдачи не выбран'}
                    </div>
                    {yandexPickupChosen ? (
                        <div className="mt-2 space-y-1 text-bark-800">
                            {pvzLineDisplay ? <p>{pvzLineDisplay}</p> : null}
                            {yandexFeeValid ? (
                                <p className="text-bark-600">
                                    Доставка до ПВЗ: {Number(resolvedYandexFee).toFixed(2)} ₽
                                </p>
                            ) : yandexFeePrefetching ? (
                                <p className="text-bark-600">Считаем стоимость доставки до выбранного пункта…</p>
                            ) : (
                                <p className="text-bark-600">
                                    Если сумма не появилась, откройте карту и нажмите «Продолжить» у пункта — тот же
                                    расчёт, что у виджета.
                                </p>
                            )}
                        </div>
                    ) : (
                        <p className="mt-1 text-bark-700">
                            Откройте карту, выберите пункт и нажмите «Продолжить» в виджете — окно закроется само.
                        </p>
                    )}
                </div>
                <button
                    type="button"
                    className="lut-btn-pine w-full sm:w-auto"
                    disabled={!yandexShipStationReady}
                    onClick={openPvzModal}
                >
                    {yandexPickupChosen ? 'Изменить пункт на карте' : 'Выбрать на карте'}
                </button>
            </>
        ) : null;

    return (
        <div>
            <h1 className="text-3xl font-semibold tracking-tight text-bark-900">Оформление заказа</h1>
            {error && (
                <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-800">
                    {error}
                </div>
            )}

            <div className="mt-8 grid gap-8 lg:grid-cols-2">
                <div className="flex flex-col gap-6">
                    <div className="lut-card flex flex-col gap-3">
                        <h2 className="text-lg font-semibold text-bark-900">Способ доставки</h2>
                        <label className="flex cursor-pointer items-start gap-3 text-sm text-bark-800">
                            <input
                                type="radio"
                                name="delivery-kind"
                                className="mt-1"
                                checked={deliveryType === DELIVERY_MANUAL}
                                onChange={() => onDeliveryKindChange(DELIVERY_MANUAL)}
                            />
                            <span>
                                <span className="font-medium">По договорённости</span>
                                <span className="mt-0.5 block text-bark-500">
                                    Укажите адрес доставки вручную.
                                </span>
                            </span>
                        </label>
                        <label className="flex cursor-pointer items-start gap-3 text-sm text-bark-800">
                            <input
                                type="radio"
                                name="delivery-kind"
                                className="mt-1"
                                checked={deliveryType === DELIVERY_YANDEX}
                                onChange={() => onDeliveryKindChange(DELIVERY_YANDEX)}
                            />
                            <span>
                                <span className="font-medium">Яндекс.Доставка (ПВЗ / постамат)</span>
                                <span className="mt-0.5 block text-bark-500">
                                    Выбор пункта на карте; стоимость доставки входит в сумму заказа.
                                </span>
                            </span>
                        </label>
                    </div>

                    <ShipmentForm
                        shipment={shipment}
                        setShipment={setShipment}
                        save={placeOrder}
                        errors={error}
                        saveDisabled={submitting || yandexFeeBlocking}
                        saveButtonTitle={placeOrderButtonTitle}
                        attemptedSubmit={attemptedSubmit}
                        pickupSlot={yandexPickupSlot}
                    />
                </div>

                <div className="lut-card h-fit">
                    <h2 className="border-b border-stone-200/80 pb-3 text-lg font-semibold text-bark-900">Состав заказа</h2>
                    <div className="pt-4">
                        {user.isAuth && authCheckoutLoading ? (
                            <p className="text-sm text-bark-500">Загрузка корзины…</p>
                        ) : linesForTotals.length === 0 ? (
                            <p className="text-bark-500">Корзина пуста.</p>
                        ) : (
                            <ul className="divide-y divide-stone-100">
                                {linesForTotals.map((line) => {
                                    const k = lineKey(line);
                                    const title = line.title || `Товар #${k}`;
                                    const qty = line.itemCount ?? 0;
                                    const subtotal = Number(line.price) * qty;
                                    const lineMax = effectiveStockCap(line.count, line);
                                    return (
                                        <li key={k} className="py-2.5 first:pt-0 sm:py-3">
                                            <div className="flex items-start gap-2 sm:items-center sm:gap-3">
                                                <div className="min-w-0 flex-1 self-start pt-0.5">
                                                    <h3 className="line-clamp-2 text-sm font-medium leading-snug text-bark-900">
                                                        {title}
                                                    </h3>
                                                    <p className="mt-0.5 text-xs tabular-nums text-bark-500">
                                                        {line.price} ₽ × {qty}
                                                    </p>
                                                    {line.itemCount >= lineMax ? (
                                                        <p className="mt-0.5 text-[11px] leading-tight text-red-600">
                                                            Макс. на складе
                                                        </p>
                                                    ) : null}
                                                </div>
                                                <div className="flex shrink-0 items-center gap-0.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleLineDecrease(line)}
                                                        className="flex h-8 w-8 touch-manipulation items-center justify-center rounded-full border border-stone-200 text-base leading-none text-bark-800 hover:bg-cream-100"
                                                    >
                                                        −
                                                    </button>
                                                    <span className="min-w-[1.5rem] text-center text-sm font-medium tabular-nums">
                                                        {line.itemCount}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleLineIncrease(line)}
                                                        disabled={line.itemCount >= lineMax}
                                                        className="flex h-8 w-8 touch-manipulation items-center justify-center rounded-full border border-stone-200 text-base leading-none text-bark-800 hover:bg-cream-100 disabled:opacity-40"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                                <div className="flex shrink-0 items-center gap-1">
                                                    <span className="text-sm font-semibold tabular-nums text-bark-900">
                                                        {subtotal.toFixed(2)} ₽
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleLineDecrease(line)}
                                                        className="touch-manipulation rounded-full p-1.5 text-lg leading-none text-red-600 hover:bg-red-50"
                                                        aria-label="Убрать одну единицу"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                        {linesForTotals.length > 0 && (
                            <div className="mt-4 space-y-2 border-t border-stone-100 pt-4 text-sm">
                                <div className="flex justify-between text-bark-600">
                                    <span>Товары</span>
                                    <span>{itemsSubtotal.toFixed(2)} ₽</span>
                                </div>
                                {deliveryType === DELIVERY_YANDEX && yandexFeeValid && resolvedYandexFee >= 0 && (
                                    <div className="flex justify-between text-bark-600">
                                        <span>Доставка до ПВЗ</span>
                                        <span>{Number(resolvedYandexFee).toFixed(2)} ₽</span>
                                    </div>
                                )}
                                <div className="flex justify-between font-semibold text-bark-900">
                                    <span>К оплате</span>
                                    <span>
                                        {(itemsSubtotal +
                                            (deliveryType === DELIVERY_YANDEX && yandexFeeValid
                                                ? resolvedYandexFee
                                                : 0)).toFixed(2)}{' '}
                                        ₽
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <YandexPvzPickerModal
                open={pvzModalOpen}
                onClose={() => setPvzModalOpen(false)}
                stationId={shipFromStationId}
                searchPreset={pvzModalSearchPreset}
                remountKey={pvzModalRemountKey}
                onPicked={onYandexSelection}
            />
        </div>
    );
});

export default OrderCreation;
